import React, { useEffect, useState } from 'react';
import { useDashboardData } from '../components/DashboardShell';
import { useLocale } from '../context/LocaleContext';
import { dashboardApi } from '../api/dashboardApi';
import { normalizeDashboardLocale } from '../i18n/messages';
import MainPanel from '../components/layout/MainPanel';
import './Dashboard.css';

function SettingsForm() {
  const { data, loading, reload } = useDashboardData();
  const { t, setLocale } = useLocale();
  const [settings, setSettings] = useState({
    botName: 'AI Demo Assistant',
    greeting: '',
    responseSpeed: 'normal',
    voiceEnabled: true,
    screenShareEnabled: true,
    accountName: '',
    siteLanguage: '',
    incomingEmailDomain: '',
    supportEmail: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    if (!data?.settings) return;
    const s = data.settings;
    setSettings({
      botName: s.botName || 'AI Demo Assistant',
      greeting: s.greeting || '',
      responseSpeed: s.responseSpeed || 'normal',
      voiceEnabled: s.voiceEnabled !== false,
      screenShareEnabled: s.screenShareEnabled !== false,
      accountName: s.accountName || '',
      siteLanguage: s.siteLanguage || '',
      incomingEmailDomain: s.incomingEmailDomain || '',
      supportEmail: s.supportEmail || '',
    });
  }, [data]);

  if (!data && loading) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setSaveMsg(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await dashboardApi('/api/dashboard/settings', {
        method: 'PATCH',
        body: JSON.stringify(settings),
      });
      setSaveMsg({ type: 'ok' });
      await reload();
      const n = normalizeDashboardLocale(settings.siteLanguage);
      if (n) setLocale(n);
    } catch (e) {
      setSaveMsg({ type: 'err', text: e.message || t('settings.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {saveMsg && (
        <div className={`settings-flash ${saveMsg.type === 'ok' ? 'settings-flash--ok' : 'settings-flash--err'}`} role="status">
          {saveMsg.type === 'ok' ? t('settings.savedOk') : saveMsg.text}
        </div>
      )}

      <div className="custio-settings-section">
        <h2 className="custio-settings-section__title">{t('settings.botSection')}</h2>
        <div className="settings-fields">
          <label className="custio-field">
            <span className="custio-field__label">{t('settings.botName')}</span>
            <input
              type="text"
              name="botName"
              value={settings.botName}
              onChange={handleChange}
              className="custio-field__input"
            />
          </label>

          <label className="custio-field">
            <span className="custio-field__label">{t('settings.greeting')}</span>
            <textarea
              name="greeting"
              value={settings.greeting}
              onChange={handleChange}
              rows={3}
              className="custio-field__textarea"
            />
          </label>

          <label className="custio-field">
            <span className="custio-field__label">{t('settings.responseSpeed')}</span>
            <select
              name="responseSpeed"
              value={settings.responseSpeed}
              onChange={handleChange}
              className="custio-field__select"
            >
              <option value="fast">{t('settings.speedFast')}</option>
              <option value="normal">{t('settings.speedNormal')}</option>
              <option value="slow">{t('settings.speedSlow')}</option>
            </select>
          </label>
        </div>
      </div>

      <div className="custio-settings-section">
        <h2 className="custio-settings-section__title">{t('settings.workspaceSection')}</h2>
        <div className="settings-fields">
          <label className="custio-field">
            <span className="custio-field__label">{t('settings.accountName')}</span>
            <input
              type="text"
              name="accountName"
              value={settings.accountName}
              onChange={handleChange}
              className="custio-field__input"
            />
          </label>
          <label className="custio-field">
            <span className="custio-field__label">{t('settings.siteLanguage')}</span>
            <input
              type="text"
              name="siteLanguage"
              value={settings.siteLanguage}
              onChange={handleChange}
              placeholder={t('settings.siteLanguagePh')}
              className="custio-field__input"
            />
          </label>
          <label className="custio-field">
            <span className="custio-field__label">{t('settings.incomingDomain')}</span>
            <input
              type="text"
              name="incomingEmailDomain"
              value={settings.incomingEmailDomain}
              onChange={handleChange}
              className="custio-field__input"
            />
          </label>
          <label className="custio-field">
            <span className="custio-field__label">{t('settings.supportEmail')}</span>
            <input
              type="text"
              name="supportEmail"
              value={settings.supportEmail}
              onChange={handleChange}
              className="custio-field__input"
            />
          </label>
        </div>
      </div>

      <div className="custio-settings-section">
        <h2 className="custio-settings-section__title">{t('settings.featuresSection')}</h2>
        <div className="settings-toggles">
          <label className="settings-toggle">
            <div>
              <div className="settings-toggle__title">{t('settings.voiceTitle')}</div>
              <div className="settings-toggle__hint">{t('settings.voiceHint')}</div>
            </div>
            <input
              type="checkbox"
              name="voiceEnabled"
              checked={settings.voiceEnabled}
              onChange={handleChange}
              className="settings-checkbox"
            />
          </label>

          <label className="settings-toggle">
            <div>
              <div className="settings-toggle__title">{t('settings.screenTitle')}</div>
              <div className="settings-toggle__hint">{t('settings.screenHint')}</div>
            </div>
            <input
              type="checkbox"
              name="screenShareEnabled"
              checked={settings.screenShareEnabled}
              onChange={handleChange}
              className="settings-checkbox"
            />
          </label>
        </div>
      </div>

      <div className="settings-actions">
        <button type="button" className="custio-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? t('settings.saving') : t('settings.update')}
        </button>
      </div>
    </>
  );
}

export default function AccountSettingsPage() {
  const { data } = useDashboardData();
  const { t } = useLocale();
  const profile = data?.profile;
  return (
    <MainPanel
      title={t('settings.title')}
      subtitle={
        profile?.displayName ? t('settings.signedInAs', { name: profile.displayName }) : t('settings.general')
      }
    >
      <SettingsForm />
    </MainPanel>
  );
}
